import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Users,
  Buildings,
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  Warning,
  CheckCircle,
  UserCirclePlus,
  ShieldCheck,
  ArrowsClockwise,
  Eye,
  EyeSlash,
  Spinner,
  Funnel,
  CaretDown,
  Shield,
  UserGear
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ROLE_LABELS = {
  buyer: 'Buyer',
  department_head: 'Department Head',
  purchasing_manager: 'Purchasing Manager',
  receiver: 'Receiver',
  storekeeper: 'Storekeeper',
  vendor: 'Vendor',
  freight_forwarder: 'Freight Forwarder',
  financial_controller: 'Financial Controller',
  internal_requester: 'Internal Requester',
  system_admin: 'System Admin'
};

const ROLE_COLORS = {
  buyer: 'bg-blue-100 text-blue-700',
  department_head: 'bg-purple-100 text-purple-700',
  purchasing_manager: 'bg-emerald-100 text-emerald-700',
  receiver: 'bg-orange-100 text-orange-700',
  storekeeper: 'bg-cyan-100 text-cyan-700',
  vendor: 'bg-pink-100 text-pink-700',
  freight_forwarder: 'bg-yellow-100 text-yellow-700',
  financial_controller: 'bg-red-100 text-red-700',
  internal_requester: 'bg-slate-100 text-slate-700',
  system_admin: 'bg-indigo-100 text-indigo-700'
};

const AUTH_METHOD_LABELS = {
  local: 'Email/Password',
  google: 'Google SSO',
  microsoft: 'Microsoft SSO'
};

const UserManagementPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Companies state
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  
  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    company_id: '',
    roles: [],
    department: '',
    auth_method: 'local',
    password: '',
    notes: ''
  });
  
  // Role conflict state
  const [roleConflicts, setRoleConflicts] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);

  const isSuperAdmin = user?.is_super_admin || user?.role === 'system_admin';

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/companies`, getAuthHeaders());
      setCompanies(res.data);
      if (res.data.length > 0 && !selectedCompany) {
        setSelectedCompany(res.data[0]);
      }
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load companies');
      }
    }
  }, [getAuthHeaders, selectedCompany]);

  const fetchUsers = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/users`;
      const params = new URLSearchParams();
      if (filterCompany) params.append('company_id', filterCompany);
      if (filterRole) params.append('role', filterRole);
      if (filterStatus === 'active') params.append('is_active', 'true');
      else if (filterStatus === 'inactive') params.append('is_active', 'false');
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, getAuthHeaders());
      setUsers(res.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load users');
      }
    }
  }, [getAuthHeaders, filterCompany, filterRole, filterStatus]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/roles`, getAuthHeaders());
      setAvailableRoles(res.data.roles || []);
      setRoleConflicts(res.data.conflicts || []);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCompanies(), fetchUsers(), fetchRoles()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCompanies, fetchUsers, fetchRoles]);

  // Filter users based on search
  useEffect(() => {
    let filtered = users;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(term) ||
        u.full_name?.toLowerCase().includes(term) ||
        u.company_name?.toLowerCase().includes(term)
      );
    }
    setFilteredUsers(filtered);
  }, [users, searchTerm]);

  // Check for role conflicts in real-time
  const checkRoleConflicts = (roles) => {
    const warnings = [];
    for (const conflict of roleConflicts) {
      if (roles.includes(conflict.role1) && roles.includes(conflict.role2)) {
        warnings.push(conflict);
      }
    }
    return warnings;
  };

  // Company handlers
  const handleCreateCompany = async () => {
    if (!editingCompany?.name?.trim() || !editingCompany?.code?.trim()) {
      toast.error('Company name and code are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingCompany.id) {
        await axios.put(`${API_URL}/api/admin/companies/${editingCompany.id}`, editingCompany, getAuthHeaders());
        toast.success('Company updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/companies`, editingCompany, getAuthHeaders());
        toast.success('Company created successfully');
      }
      setCompanyDialogOpen(false);
      setEditingCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    } finally {
      setSubmitting(false);
    }
  };

  const openCompanyDialog = (company = null) => {
    setEditingCompany(company || {
      name: '',
      code: '',
      industry: 'hospitality',
      address: '',
      contact_email: '',
      contact_phone: '',
      subscription_tier: 'single_property',
      notes: ''
    });
    setCompanyDialogOpen(true);
  };

  // User handlers
  const handleCreateUser = async () => {
    const userData = editingUser || newUser;
    if (!userData.email?.trim() || !userData.full_name?.trim()) {
      toast.error('Email and full name are required');
      return;
    }
    if (!userData.company_id) {
      toast.error('Please select a company');
      return;
    }
    if (!userData.roles?.length) {
      toast.error('Please select at least one role');
      return;
    }
    if (userData.auth_method === 'local' && !editingUser && !userData.password) {
      toast.error('Password is required for local authentication');
      return;
    }

    setSubmitting(true);
    try {
      if (editingUser?.id) {
        const updateData = {
          full_name: userData.full_name,
          roles: userData.roles,
          department: userData.department,
          is_active: userData.is_active,
          is_company_admin: userData.is_company_admin,
          notes: userData.notes
        };
        const res = await axios.put(`${API_URL}/api/admin/users/${editingUser.id}`, updateData, getAuthHeaders());
        if (res.data.role_conflict_warnings?.length) {
          toast.warning(`User updated with role conflict warnings`);
        } else {
          toast.success('User updated successfully');
        }
      } else {
        const res = await axios.post(`${API_URL}/api/admin/users`, userData, getAuthHeaders());
        if (res.data.role_conflict_warnings?.length) {
          toast.warning(`User created with role conflict warnings`);
        } else {
          toast.success('User created successfully');
        }
      }
      setUserDialogOpen(false);
      setEditingUser(null);
      setNewUser({
        email: '',
        full_name: '',
        company_id: '',
        roles: [],
        department: '',
        auth_method: 'local',
        password: '',
        notes: ''
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const openUserDialog = (user = null) => {
    if (user) {
      setEditingUser({ ...user });
      setNewUser({
        email: '',
        full_name: '',
        company_id: '',
        roles: [],
        department: '',
        auth_method: 'local',
        password: '',
        notes: ''
      });
    } else {
      setEditingUser(null);
      setNewUser({
        email: '',
        full_name: '',
        company_id: companies[0]?.id || '',
        roles: [],
        department: '',
        auth_method: 'local',
        password: '',
        notes: ''
      });
    }
    setUserDialogOpen(true);
  };

  const handleDeactivateUser = async (userId) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, getAuthHeaders());
      toast.success('User deactivated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/reactivate`, {}, getAuthHeaders());
      toast.success('User reactivated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    }
  };

  const handleToggleCompanyAdmin = async (userId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/make-company-admin?is_admin=${!currentStatus}`, {}, getAuthHeaders());
      toast.success(currentStatus ? 'Admin status revoked' : 'Admin status granted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const toggleRole = (role, isEdit = false) => {
    const userData = isEdit ? editingUser : newUser;
    const setUserData = isEdit ? setEditingUser : setNewUser;
    const currentRoles = userData.roles || [];
    
    if (currentRoles.includes(role)) {
      setUserData({ ...userData, roles: currentRoles.filter(r => r !== role) });
    } else {
      setUserData({ ...userData, roles: [...currentRoles, role] });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#8B5CF6] border-t-transparent"></div>
      </div>
    );
  }

  const currentUserData = editingUser || newUser;
  const currentRoleWarnings = checkRoleConflicts(currentUserData.roles || []);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center">
            <UserGear size={24} className="text-[#8B5CF6]" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              User Management
            </h1>
            <p className="text-slate-500">
              {isSuperAdmin ? 'Manage companies and users across the platform' : 'Manage users in your company'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs - Super Admin sees both, Company Admin sees Users only */}
      <div className="flex gap-2 border-b border-slate-200">
        {isSuperAdmin && (
          <button
            onClick={() => setActiveTab('companies')}
            className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'companies'
                ? 'text-[#8B5CF6] border-b-2 border-[#8B5CF6]'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-companies"
          >
            <Buildings size={18} />
            Companies ({companies.length})
          </button>
        )}
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'users'
              ? 'text-[#8B5CF6] border-b-2 border-[#8B5CF6]'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-users"
        >
          <Users size={18} />
          Users ({users.length})
        </button>
      </div>

      {/* Companies Tab */}
      {activeTab === 'companies' && isSuperAdmin && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Button onClick={() => openCompanyDialog()} className="bg-[#8B5CF6] hover:bg-[#7C3AED]" data-testid="add-company-btn">
              <Plus size={16} className="mr-2" />
              Add Company
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <table className="w-full" data-testid="companies-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Company Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Tier</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Users</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {companies.map(company => (
                  <tr key={company.id} className="hover:bg-slate-50" data-testid={`company-row-${company.id}`}>
                    <td className="px-4 py-3 font-mono text-[#8B5CF6] font-semibold">{company.code}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{company.name}</div>
                      {company.address && <div className="text-xs text-slate-500">{company.address}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{company.contact_email || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {company.subscription_tier?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-medium">{company.user_count}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        company.status === 'active' ? 'bg-green-100 text-green-700' :
                        company.status === 'suspended' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {company.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openCompanyDialog(company)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-[#8B5CF6]"
                          title="Edit company"
                          data-testid={`edit-company-${company.id}`}
                        >
                          <PencilSimple size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {companies.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Buildings size={48} className="mx-auto mb-4 text-slate-300" />
                <p>No companies yet</p>
                <p className="text-sm mt-2">Click "Add Company" to onboard your first client</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
            {isSuperAdmin && (
              <select
                value={filterCompany}
                onChange={(e) => { setFilterCompany(e.target.value); }}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="filter-company"
              >
                <option value="">All Companies</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="filter-role"
            >
              <option value="">All Roles</option>
              {availableRoles.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="filter-status"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <Button onClick={() => fetchUsers()} variant="outline" size="sm">
              <ArrowsClockwise size={16} className="mr-1" />
              Refresh
            </Button>
            <Button onClick={() => openUserDialog()} className="bg-[#8B5CF6] hover:bg-[#7C3AED] ml-auto" data-testid="add-user-btn">
              <UserCirclePlus size={16} className="mr-2" />
              Add User
            </Button>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Roles</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Auth</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Admin</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map(u => (
                    <tr key={u.id} className={`hover:bg-slate-50 ${!u.is_active ? 'opacity-60' : ''}`} data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{u.full_name}</div>
                        <div className="text-sm text-slate-500">{u.email}</div>
                        {u.department && <div className="text-xs text-slate-400">{u.department}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{u.company_name}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.roles?.map(role => (
                            <span key={role} className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[role] || 'bg-slate-100 text-slate-700'}`}>
                              {ROLE_LABELS[role] || role}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {AUTH_METHOD_LABELS[u.auth_method] || u.auth_method}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {u.is_active ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircle size={12} className="mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-600">
                            <EyeSlash size={12} className="mr-1" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {u.is_super_admin ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700" title="Super Admin">
                            <Shield size={14} />
                          </span>
                        ) : u.is_company_admin ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700" title="Company Admin">
                            <ShieldCheck size={14} />
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openUserDialog(u)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-[#8B5CF6]"
                            title="Edit user"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <PencilSimple size={16} />
                          </button>
                          {!u.is_company_admin && isSuperAdmin && (
                            <button
                              onClick={() => handleToggleCompanyAdmin(u.id, u.is_company_admin)}
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-purple-600"
                              title="Make company admin"
                              data-testid={`admin-user-${u.id}`}
                            >
                              <ShieldCheck size={16} />
                            </button>
                          )}
                          {u.is_active ? (
                            <button
                              onClick={() => handleDeactivateUser(u.id)}
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-red-600"
                              title="Deactivate user"
                              data-testid={`deactivate-user-${u.id}`}
                            >
                              <Trash size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivateUser(u.id)}
                              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-green-600"
                              title="Reactivate user"
                              data-testid={`reactivate-user-${u.id}`}
                            >
                              <ArrowsClockwise size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredUsers.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Users size={48} className="mx-auto mb-4 text-slate-300" />
                <p>No users found</p>
                <p className="text-sm mt-2">
                  {searchTerm ? 'Try adjusting your search or filters' : 'Click "Add User" to create a new user'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Buildings size={20} className="text-[#8B5CF6]" />
              {editingCompany?.id ? 'Edit Company' : 'Add New Company'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={editingCompany?.name || ''}
                  onChange={(e) => setEditingCompany({...editingCompany, name: e.target.value})}
                  placeholder="Acme Resort"
                  data-testid="company-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={editingCompany?.code || ''}
                  onChange={(e) => setEditingCompany({...editingCompany, code: e.target.value.toUpperCase()})}
                  placeholder="ACME"
                  maxLength={10}
                  disabled={!!editingCompany?.id}
                  data-testid="company-code"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={editingCompany?.address || ''}
                onChange={(e) => setEditingCompany({...editingCompany, address: e.target.value})}
                placeholder="123 Beach Road, Barbados"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={editingCompany?.contact_email || ''}
                  onChange={(e) => setEditingCompany({...editingCompany, contact_email: e.target.value})}
                  placeholder="admin@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  value={editingCompany?.contact_phone || ''}
                  onChange={(e) => setEditingCompany({...editingCompany, contact_phone: e.target.value})}
                  placeholder="+1 246 123 4567"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Subscription Tier</Label>
              <select
                value={editingCompany?.subscription_tier || 'single_property'}
                onChange={(e) => setEditingCompany({...editingCompany, subscription_tier: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                data-testid="company-tier"
              >
                <option value="individual">Individual</option>
                <option value="single_property">Single Property</option>
                <option value="multi_property">Multi Property</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <textarea
                value={editingCompany?.notes || ''}
                onChange={(e) => setEditingCompany({...editingCompany, notes: e.target.value})}
                placeholder="Internal notes about this company..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setCompanyDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateCompany} disabled={submitting} className="bg-[#8B5CF6] hover:bg-[#7C3AED]" data-testid="save-company-btn">
                {submitting ? <Spinner size={16} className="animate-spin mr-2" /> : null}
                {editingCompany?.id ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCirclePlus size={20} className="text-[#8B5CF6]" />
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={currentUserData.email || ''}
                  onChange={(e) => editingUser 
                    ? setEditingUser({...editingUser, email: e.target.value})
                    : setNewUser({...newUser, email: e.target.value})
                  }
                  placeholder="user@company.com"
                  disabled={!!editingUser}
                  data-testid="user-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={currentUserData.full_name || ''}
                  onChange={(e) => editingUser 
                    ? setEditingUser({...editingUser, full_name: e.target.value})
                    : setNewUser({...newUser, full_name: e.target.value})
                  }
                  placeholder="John Smith"
                  data-testid="user-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company *</Label>
                <select
                  value={currentUserData.company_id || ''}
                  onChange={(e) => editingUser 
                    ? setEditingUser({...editingUser, company_id: e.target.value})
                    : setNewUser({...newUser, company_id: e.target.value})
                  }
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  disabled={!!editingUser || !isSuperAdmin}
                  data-testid="user-company"
                >
                  <option value="">Select Company</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={currentUserData.department || ''}
                  onChange={(e) => editingUser 
                    ? setEditingUser({...editingUser, department: e.target.value})
                    : setNewUser({...newUser, department: e.target.value})
                  }
                  placeholder="Procurement"
                />
              </div>
            </div>

            {!editingUser && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Authentication Method</Label>
                  <select
                    value={newUser.auth_method}
                    onChange={(e) => setNewUser({...newUser, auth_method: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="user-auth-method"
                  >
                    <option value="local">Email/Password</option>
                    <option value="google">Google SSO</option>
                    <option value="microsoft">Microsoft SSO</option>
                  </select>
                </div>
                {newUser.auth_method === 'local' && (
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      placeholder="••••••••"
                      data-testid="user-password"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Roles Selection */}
            <div className="space-y-2">
              <Label>Roles * (select one or more)</Label>
              <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg max-h-[200px] overflow-y-auto">
                {availableRoles.map(role => {
                  const isSelected = (currentUserData.roles || []).includes(role.value);
                  return (
                    <label 
                      key={role.value}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected ? 'bg-[#8B5CF6]/10 border border-[#8B5CF6]/30' : 'hover:bg-slate-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRole(role.value, !!editingUser)}
                        className="rounded border-slate-300 text-[#8B5CF6] focus:ring-[#8B5CF6]"
                      />
                      <span className="text-sm">{role.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Role Conflict Warnings */}
            {currentRoleWarnings.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Role Conflict Warning</p>
                    {currentRoleWarnings.map((w, i) => (
                      <p key={i} className="text-sm text-amber-700 mt-1">{w.warning}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {editingUser && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-700">Company Admin</p>
                  <p className="text-sm text-slate-500">Can manage users in their company</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingUser({...editingUser, is_company_admin: !editingUser.is_company_admin})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    editingUser.is_company_admin ? 'bg-[#8B5CF6]' : 'bg-slate-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editingUser.is_company_admin ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <textarea
                value={currentUserData.notes || ''}
                onChange={(e) => editingUser 
                  ? setEditingUser({...editingUser, notes: e.target.value})
                  : setNewUser({...newUser, notes: e.target.value})
                }
                placeholder="Internal notes about this user..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => { setUserDialogOpen(false); setEditingUser(null); }} data-testid="cancel-user-btn">
                Cancel
              </Button>
              <Button 
                onClick={handleCreateUser} 
                disabled={submitting} 
                className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
                data-testid="save-user-btn"
              >
                {submitting ? <Spinner size={16} className="animate-spin mr-2" /> : null}
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
